import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GroupCtx = createContext(null);
const STORAGE_KEY = "aviary_active_group_id";

export function GroupProvider({ children }) {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null); // null=loading, [] empty, [...] loaded
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!user) {
      setGroups(null);
      return;
    }
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
      // Pick active group: stored one if still valid, else first
      const validIds = new Set(data.map((g) => g.id));
      let current = localStorage.getItem(STORAGE_KEY) || "";
      if (!validIds.has(current)) {
        current = data[0]?.id || "";
      }
      setActiveId(current);
      if (current) localStorage.setItem(STORAGE_KEY, current);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      setGroups([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Attach X-Group-Id header to all axios requests
  useEffect(() => {
    const id = api.interceptors.request.use((config) => {
      const aid = activeId || localStorage.getItem(STORAGE_KEY) || "";
      if (aid) {
        config.headers = config.headers || {};
        config.headers["X-Group-Id"] = aid;
      }
      return config;
    });
    return () => { api.interceptors.request.eject(id); };
  }, [activeId]);

  const switchGroup = (id) => {
    setActiveId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
  };

  const createGroup = async (name) => {
    setError("");
    try {
      const { data } = await api.post("/groups", { name });
      await refresh();
      switchGroup(data.id);
      return data;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      return null;
    }
  };

  const joinGroup = async (invite_code) => {
    setError("");
    try {
      const { data } = await api.post("/groups/join", { invite_code });
      await refresh();
      switchGroup(data.id);
      return data;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      return null;
    }
  };

  const leaveGroup = async (id) => {
    setError("");
    try {
      await api.post(`/groups/${id}/leave`);
      if (activeId === id) {
        localStorage.removeItem(STORAGE_KEY);
        setActiveId("");
      }
      await refresh();
      return true;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      return false;
    }
  };

  const deleteGroup = async (id) => {
    setError("");
    try {
      await api.delete(`/groups/${id}`);
      if (activeId === id) {
        localStorage.removeItem(STORAGE_KEY);
        setActiveId("");
      }
      await refresh();
      return true;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      return false;
    }
  };

  const activeGroup = groups?.find((g) => g.id === activeId) || null;

  return (
    <GroupCtx.Provider value={{
      groups, activeGroup, activeId,
      switchGroup, createGroup, joinGroup, leaveGroup, deleteGroup,
      refresh, error, setError,
    }}>
      {children}
    </GroupCtx.Provider>
  );
}

export const useGroups = () => useContext(GroupCtx);
